import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { SeekHistorySession } from "src/types/seek";
import styles from "./sessions.module.scss";
import InfiniteScroll from "src/components/infiniteScroll";
import moment from "moment";
import _ from "lodash";
import { useDispatch } from "react-redux";
import {
  deleteSeekSession,
  deleteSeekSessions,
  fetchSeekSession,
  fetchSeekSessions,
} from "src/redux/actions/seek";
import cx from "classnames";
import { useSeekAdoption } from "src/redux/actions/adoption/seek";
import EmptyMessage from "src/components/emptyMessage";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";

export const SeekSessions: React.FC = () => {
  const dispatch = useDispatch();

  const [sessions, setSessions] = useState<SeekHistorySession[]>([]);
  const { trackSeekHistoryDeleted } = useSeekAdoption();
  
  const seekSessions = useSelector((state: any) => state.seek.sessions.items);
  const currentSeekSessionId = useSelector((state: any) => state.seek.session.item.id);
  const loading = useSelector((state: any) => state.seek.sessions.loading);
  const currentPage = useSelector(
    (state: any) => state.seek.sessions.currentPage,
  );
  const totalPages = useSelector(
    (state: any) => state.seek.sessions.totalPages,
  );
  const deletingSessions = useSelector(
    (state: any) => state.seek.sessions.deleting,
  );
  const deletedSessions = useSelector(
    (state: any) => state.seek.sessions.deleted,
  )

  const deleteSeekSessionsProps: IUseFetchProps = {
    action: deleteSeekSessions,
    loading: deletingSessions,
    succeeded: deletedSessions,
    response: seekSessions,
    error: null
  };

  const { handleFetch: deleteSeekSessionsRequest } = useFetch(deleteSeekSessionsProps);

  useEffect(() => {
    if (seekSessions) {
      setSessions(seekSessions);
    }
  }, [seekSessions]);

  const handleLoadMoreSessions = () => {
    dispatch(fetchSeekSessions({ page: currentPage + 1, limit: 15 }));
  };

  const processSessions = (records: SeekHistorySession[]) => {
    const groupedSessions = _.groupBy(records, (session) => {
      const createDate = moment(session.createdDate);

      if (createDate.isSame(moment(), "day")) {
        return "Today";
      } else if (createDate.isSame(moment().subtract(1, "day"), "day")) {
        return "Yesterday";
      }

      return "Older";
    });

    return groupedSessions;
  };

  const renderSessions = () => {
    if (!sessions || sessions.length === 0) {
      return loading ? (
        <div className={cx(styles.loading, styles.messageContainer)}>Loading sessions...</div>
      ) : (
        <div className={cx(styles.messageContainer)}>
          <EmptyMessage
            width="auto"
            image={"/images/seek/seekHistory.svg"}
            heading="No sessions found"
            message=""
          ></EmptyMessage>
        </div>
      );
    }

    const groupedSessions = processSessions(sessions);

    return Object.entries(groupedSessions).map(([group, items]) => (
      <div key={group} className={styles.group}>
        <div className={styles.groupHeaderContainer}>
          <div className={styles.groupHeader}>{group}</div>
          <div className={styles.separator}></div>
        </div>
        {items.map((session: SeekHistorySession) => (
          <div className={styles.session} key={session.id}>
            <div
              className={cx(
                styles.question, {
                  [styles.selectedQuestion]: session.id === currentSeekSessionId,
                }
              )}
              onClick={() => {                
                dispatch(fetchSeekSession(session));
              }}
            >
              {session.name}
              <div className={styles.createdDate}>
                {moment(session.createdDate).fromNow()}
              </div>
            </div>
            <button
              className={styles.delete}
              onClick={() => dispatch(deleteSeekSession({ id: session.id }))}
            >
              <i className="paperflite-delete" />
            </button>
          </div>
        ))}
      </div>
    ));
  };

  return (
    <>
      <InfiniteScroll
        className={cx(styles.scroll, styles.sessions)}
        loading={loading}
        hasMore={currentPage < totalPages}
        loadMore={handleLoadMoreSessions}
        loader={() => {
          return <div className={styles.loader}>Loading...</div>;
        }}
      >
        {renderSessions()}
      </InfiniteScroll>
      {_.size(seekSessions) > 0 && (
        <div
          className={cx(
            styles.clearSessions,
            deletingSessions && styles.deleting,
          )}
        >
          <span onClick={() => {
            deleteSeekSessionsRequest({}).then(() => {
              trackSeekHistoryDeleted();
            })
          }}>
            <i className="paperflite-delete"></i>
            <span>{deletingSessions ? "clearing..." : "clear all"}</span>
          </span>
        </div>
      )}
    </>
  );
};
